import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { endpoints } from '../api/client.js';

const PinsContext = createContext(null);

export function PinsProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [sites, setSites] = useState([]);
  const [limit, setLimit] = useState(5);
  const [pendingSite, setPendingSite] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback((signal) => {
    setStatus('loading');
    setError(null);
    return endpoints.pins(signal)
      .then((result) => {
        setSites(Array.isArray(result?.sites) ? result.sites : []);
        setLimit(Number.isInteger(result?.limit) ? result.limit : 5);
        setStatus('ready');
      })
      .catch((loadError) => {
        if (loadError?.name === 'AbortError') return;
        setError(loadError);
        setStatus('error');
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const toggle = useCallback(async (siteName) => {
    if (status !== 'ready' || pendingSite) return false;

    const wasPinned = sites.includes(siteName);
    if (!wasPinned && sites.length >= limit) {
      setError(new Error(`You can pin up to ${limit} sites. Unpin one before adding another.`));
      return false;
    }

    const previous = sites;
    const optimistic = wasPinned
      ? sites.filter((name) => name !== siteName)
      : [...sites, siteName];

    setSites(optimistic);
    setPendingSite(siteName);
    setError(null);

    try {
      const result = wasPinned
        ? await endpoints.unpinSite(siteName)
        : await endpoints.pinSite(siteName);
      setSites(Array.isArray(result?.sites) ? result.sites : optimistic);
      setLimit(Number.isInteger(result?.limit) ? result.limit : limit);
      return true;
    } catch (mutationError) {
      setSites(previous);
      setError(mutationError);
      return false;
    } finally {
      setPendingSite(null);
    }
  }, [limit, pendingSite, sites, status]);

  const value = useMemo(() => ({
    status,
    sites,
    limit,
    pendingSite,
    error,
    isPinned: (siteName) => sites.includes(siteName),
    toggle,
    refresh: () => load(),
    clearError: () => setError(null),
  }), [error, limit, load, pendingSite, sites, status, toggle]);

  return <PinsContext.Provider value={value}>{children}</PinsContext.Provider>;
}

export function usePins() {
  const context = useContext(PinsContext);
  if (!context) throw new Error('usePins must be used inside PinsProvider');
  return context;
}
